import { useMemo } from "react";
import { useQuery } from "react-query";

import { getTargetDetails } from "api/targets";
import { STALE_TIME_5_MINUTES, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";
import { normalizeGoal } from "api/adapters/goalAdapters";

const useGetDetailTarget = (targetId: number) => {
  const {
    data: response,
    error,
    isLoading,
    ...rest
  } = useQuery([GET_TARGET_BY_ID, targetId], () => getTargetDetails(targetId), {
    enabled: !!targetId,
    staleTime: STALE_TIME_5_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // normalizeGoal is a TEMPORARY workaround for the BE enum-casing bug on the
  // embedded `goal` (ANOW-6956, same bug as the standalone Goal endpoint) —
  // once that ticket is resolved, drop the normalization (and the
  // goalAdapters import) and return `response?.data || null` directly.
  // Guarded for malformed responses where `goal` is unexpectedly missing
  // despite the contract. Memoized on the cached response so consumers keep
  // referential stability across re-renders instead of receiving a fresh
  // object each time.
  const target = useMemo(
    () =>
      response?.data
        ? {
            ...response.data,
            goal: response.data.goal ? normalizeGoal(response.data.goal) : response.data.goal,
          }
        : null,
    [response?.data]
  );

  return {
    target,
    errorTarget: error,
    isTargetLoading: isLoading,
    ...rest,
  };
};

export default useGetDetailTarget;
